import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { fetchAdminReferralDetail } from "../api/fetchAdminReferrals";
import { REFERRAL_UNLOCK_SCORE, REFERRAL_UNLOCK_RULE_SHORT } from "../constants";
import {
  formatDaysSince,
  formatReferralCash,
  formatReferralDate,
  referralStatusMeta,
  requirementLabel,
} from "../referralHelpers";

function DetailStat({ label, value }) {
  return (
    <div className="amf-desk-stat">
      <span className="amf-desk-stat-val">{value}</span>
      <span className="amf-desk-stat-key">{label}</span>
    </div>
  );
}

function UserCard({ role, user, extra }) {
  if (!user) return null;
  return (
    <div className="arf-user-card">
      <p className="arf-user-card-role">{role}</p>
      <p className="arf-user-card-name">{user.name || "User"}</p>
      <p className="arf-user-card-meta">
        {user.contact || "No contact"}
        {user.inviteCode ? ` · ${user.inviteCode}` : ""}
      </p>
      {extra ? <p className="arf-user-card-stat">{extra}</p> : null}
    </div>
  );
}

function TopReferrersPanel({ referrers = [] }) {
  if (!referrers.length) return null;
  return (
    <section className="arf-panel">
      <h3 className="arf-panel-title">Top referrers</h3>
      <div className="arf-top-referrers">
        {referrers.slice(0, 6).map((row) => (
          <div key={row.referrerId} className="arf-top-row">
            <div>
              <p className="arf-top-name">{row.name || "Referrer"}</p>
              <p className="arf-top-sub">
                {row.inviteCode || "—"} · {row.total} referral{row.total === 1 ? "" : "s"}
              </p>
            </div>
            <div className="arf-top-stats">
              {formatReferralCash(row.paidCash)} paid
              {row.pending > 0 ? ` · ${row.pending} pending` : ""}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function UnlockPanel({ referral }) {
  const threshold = Math.max(
    Number(referral.unlockScoreThreshold) || 0,
    REFERRAL_UNLOCK_SCORE
  );
  const score = referral.referee?.allTimeScores ?? 0;
  const pct = Math.round(referral.progressPercent || 0);
  const requirements = [
    {
      key: "score",
      label: `${threshold} all-time score (${score}/${threshold})`,
      done: referral.scoreRequirementMet,
    },
    {
      key: "betting_activity",
      label: "Qualifying bet placed",
      done: referral.hasQualifyingBet,
    },
  ];

  return (
    <section className="arf-panel">
      <h3 className="arf-panel-title">Unlock progress</h3>
      <div className="arf-unlock-ring">
        <div className="arf-ring" style={{ "--arf-ring-pct": pct }}>
          <div className="arf-ring-inner">{pct}%</div>
        </div>
        <div className="arf-unlock-copy">
          <h4>
            {referral.rewardReady
              ? "Ready for payout"
              : referral.status === "rewarded"
                ? "Reward unlocked"
                : "Working toward unlock"}
          </h4>
          <p>
            Referee needs {threshold} all-time score and one qualifying bet before{" "}
            {formatReferralCash(referral.rewardAmount)} is credited to the referrer.
            {referral.scoreRemaining > 0
              ? ` ${referral.scoreRemaining} all-time points remaining.`
              : ""}
          </p>
        </div>
      </div>
      <div className="arf-checklist">
        {requirements.map((req) => (
          <div
            key={req.key}
            className={`arf-check ${req.done ? "arf-check-done" : ""}`}
          >
            <span className="arf-check-dot" aria-hidden />
            {req.label}
          </div>
        ))}
        {(referral.missingRequirements || [])
          .filter((k) => !requirements.some((r) => r.key === k))
          .map((key) => (
            <div key={key} className="arf-check">
              <span className="arf-check-dot" aria-hidden />
              {requirementLabel(key, threshold)}
            </div>
          ))}
      </div>
    </section>
  );
}

function PayoutPanel({ referral, rewardTransaction, referrerLifetime }) {
  if (referral.status === "rewarded") {
    return (
      <section className="arf-panel">
        <h3 className="arf-panel-title">Payout record</h3>
        <div className="arf-payout-card">
          <div className="arf-payout-amount">{formatReferralCash(referral.rewardAmount)}</div>
          <p className="arf-payout-meta">
            Credited to {referral.referrer?.name} on{" "}
            {formatReferralDate(referral.rewardedAt, { withTime: true })}
          </p>
          {rewardTransaction ? (
            <p className="arf-payout-meta">
              Ref {rewardTransaction.transactionRef} · Balance{" "}
              {formatReferralCash(rewardTransaction.balanceBefore)} →{" "}
              {formatReferralCash(rewardTransaction.balanceAfter)}
            </p>
          ) : null}
          {referral.refereeScoreAtReward != null ? (
            <p className="arf-payout-meta">
              Referee all-time score at payout: {referral.refereeScoreAtReward}
              {referral.refereeHasBettedAtReward ? " · bet confirmed" : ""}
            </p>
          ) : null}
        </div>
      </section>
    );
  }

  if (referral.status === "pending") {
    return (
      <section className="arf-panel">
        <h3 className="arf-panel-title">Pending payout</h3>
        <div className="arf-payout-card">
          <div className="arf-payout-amount">{formatReferralCash(referral.rewardAmount)}</div>
          <p className="arf-payout-meta">
            {referral.rewardReady
              ? "Eligible now — job runs every 15 min to credit referrer."
              : "Held until referee completes unlock requirements."}
          </p>
        </div>
        {referrerLifetime ? (
          <p className="arf-payout-meta" style={{ marginTop: 10 }}>
            Referrer lifetime: {referrerLifetime.totalReferrals} referrals ·{" "}
            {formatReferralCash(referrerLifetime.paidCash)} paid ·{" "}
            {formatReferralCash(referrerLifetime.pendingCash)} pending
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section className="arf-panel">
      <h3 className="arf-panel-title">Cancelled</h3>
      <p className="arf-payout-meta">
        {referral.cancelReason || "This referral was voided and will not pay out."}
      </p>
    </section>
  );
}

function TimelinePanel({ referral }) {
  const rows = [
    { label: "Signed up", value: formatReferralDate(referral.createdAt, { withTime: true }) },
    referral.referee?.joinedAt
      ? {
          label: "Referee joined",
          value: formatReferralDate(referral.referee.joinedAt, { withTime: true }),
        }
      : null,
    referral.createdAt
      ? { label: "Age", value: formatDaysSince(referral.createdAt) }
      : null,
    referral.rewardedAt
      ? {
          label: "Reward paid",
          value: formatReferralDate(referral.rewardedAt, { withTime: true }),
        }
      : null,
    referral.cancelledAt
      ? {
          label: "Cancelled",
          value: formatReferralDate(referral.cancelledAt, { withTime: true }),
        }
      : null,
    { label: "Code used", value: referral.referralCode || "—" },
  ].filter(Boolean);

  return (
    <section className="arf-panel">
      <h3 className="arf-panel-title">Timeline</h3>
      <dl className="arf-timeline">
        {rows.map((row) => (
          <div key={row.label} className="arf-timeline-row">
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export default function ReferralsFloorDetail({ referral, topReferrers, summary }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (!referral?.id) {
      setDetail(null);
      setDetailError(null);
      return undefined;
    }

    let cancelled = false;
    setLoadingDetail(true);
    setDetailError(null);

    fetchAdminReferralDetail(referral.id)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setDetail(null);
          setDetailError(
            err.response?.data?.error || err.message || "Failed to load referral detail"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [referral?.id]);

  if (!referral) {
    return (
      <section className="amf-detail amf-detail-idle arf-detail" aria-label="Select a referral">
        <div className="amf-idle">
          <p className="amf-idle-eyebrow">Referrals desk</p>
          <p className="amf-idle-title">Select a referral from the ledger</p>
          <p className="amf-idle-copy">
            Track who referred whom, pending liabilities, unlock progress, and payout history.
          </p>
          {summary ? (
            <div className="arf-rules">
              <span className="arf-rule-pill">{REFERRAL_UNLOCK_RULE_SHORT}</span>
              <span className="arf-rule-pill">
                {formatReferralCash(summary.rewardPerReferral ?? 1000)} per referral
              </span>
            </div>
          ) : null}
        </div>
        <div className="arf-detail-scroll arf-idle-top">
          <TopReferrersPanel referrers={topReferrers} />
        </div>
      </section>
    );
  }

  const full = detail?.referral || referral;
  const status = referralStatusMeta(full.status);
  const statusTone =
    status.tone === "live" ? "live" : status.tone === "warn" ? "warn" : "muted";

  return (
    <section className="amf-detail arf-detail" aria-label="Referral detail">
      <header className="amf-desk amf-desk-with-question arf-desk-header">
        <div className="arf-desk-copy">
          <p className="amf-desk-eyebrow">Referral · {full.referralCode}</p>
          <h2 className="amf-desk-title arf-desk-title">
            {full.referrer?.name || "Referrer"}
            <span className="arf-desk-arrow"> → </span>
            {full.referee?.name || "Referee"}
          </h2>
          <p className="amf-desk-meta">
            {formatReferralCash(full.rewardAmount)} ·{" "}
            <span className={`amf-row-tag amf-row-tag-${statusTone}`}>{status.label}</span>
          </p>
        </div>

        <div className="amf-desk-stats arf-desk-stats">
          <DetailStat label="All-time" value={full.referee?.allTimeScores ?? 0} />
          <DetailStat
            label="Progress"
            value={`${Math.round(full.progressPercent || 0)}%`}
          />
          <DetailStat
            label="Bet"
            value={full.hasQualifyingBet ? "Yes" : "No"}
          />
        </div>
      </header>

      <div className="amf-detail-scroll arf-detail-scroll">
        {loadingDetail ? (
          <div className="arf-detail-loading">
            <Loader2 size={18} className="am-spin" aria-hidden />
            <span>Loading detail…</span>
          </div>
        ) : null}

        {detailError ? (
          <div className="arf-error-banner" role="alert">
            <span>{detailError}</span>
          </div>
        ) : null}

        <section className="arf-panel">
          <h3 className="arf-panel-title">People</h3>
          <div className="arf-user-grid">
            <UserCard
              role="Referrer (gets paid)"
              user={full.referrer}
              extra={
                full.referrer?.cashBalance != null
                  ? `Balance: ${formatReferralCash(full.referrer.cashBalance)}`
                  : null
              }
            />
            <UserCard
              role="Referee (must unlock)"
              user={full.referee}
              extra={`All-time score: ${full.referee?.allTimeScores ?? 0} · Bet: ${
                full.hasQualifyingBet ? "placed" : "not yet"
              }`}
            />
          </div>
        </section>

        {full.status !== "cancelled" ? <UnlockPanel referral={full} /> : null}

        <PayoutPanel
          referral={full}
          rewardTransaction={detail?.rewardTransaction}
          referrerLifetime={detail?.referrerLifetime}
        />

        <TimelinePanel referral={full} />
      </div>
    </section>
  );
}
